import React, { useState } from 'react';
import { HelpCircle, AlertTriangle, XCircle, FileQuestion, RefreshCw, AlertCircle } from 'lucide-react';
import DiscrepancyHoverCard from './DiscrepancyHoverCard';

/**
 * DiscrepancyCell Component
 *
 * Renders discrepancy thumbnails with hover card for a project row.
 * Extracted to separate component to properly use React hooks.
 */
const DiscrepancyCell = ({ discrepancies = [], onDiscrepancyClick }) => {
  const [showHover, setShowHover] = useState(false);
  const containerRef = React.useRef(null);

  const discrepancyCount = discrepancies.length;
  const maxThumbnails = 3;
  const visibleDiscrepancies = discrepancies.slice(0, maxThumbnails);
  const remainingCount = Math.max(0, discrepancyCount - maxThumbnails);

  const getDiscrepancyStatusColor = (status) => {
    switch (status) {
      case 'Draft': return '#6b7280';
      case 'Waiting to Review': return '#ef4444';
      case 'Reviewed - not sent': return '#f59e0b';
      case 'Sent': return '#3b82f6';
      case 'Resolved': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getDiscrepancyTypeIcon = (type) => {
    const iconProps = { size: 14, strokeWidth: 2 };
    switch (type) {
      case 'Clarification': return <HelpCircle {...iconProps} />;
      case 'Issue': return <AlertTriangle {...iconProps} />;
      case 'Error': return <XCircle {...iconProps} />;
      case 'Missing Info': return <FileQuestion {...iconProps} />;
      case 'Change Request': return <RefreshCw {...iconProps} />;
      default: return <AlertCircle {...iconProps} />;
    }
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', minHeight: '20px' }}
      onMouseEnter={() => setShowHover(true)}
      onMouseLeave={() => setShowHover(false)}
    >
      {discrepancyCount > 0 ? (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          {visibleDiscrepancies.map((disc) => {
            if (!disc || !disc.id) return null;

            const discStatus = disc.status || disc.discrepancyStatus || 'Unknown';
            const discType = disc.type || disc.discrepancyType || 'Unknown';

            return (
              <div
                key={disc.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDiscrepancyClick) {
                    onDiscrepancyClick(e, disc.id);
                  }
                }}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: 'var(--radius-md, 6px)',
                  border: `1.5px solid ${getDiscrepancyStatusColor(discStatus)}`,
                  backgroundColor: 'var(--dark-elevated)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: getDiscrepancyStatusColor(discStatus),
                  transition: 'all 0.15s ease',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.borderWidth = '2px';
                  e.currentTarget.style.boxShadow = `0 0 0 2px ${getDiscrepancyStatusColor(discStatus)}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderWidth = '1.5px';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                title={`${disc.name || discType} (${discType})\nStatus: ${discStatus}`}
              >
                {getDiscrepancyTypeIcon(discType)}
              </div>
            );
          })}
          {remainingCount > 0 && (
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-md, 6px)',
                border: '1.5px solid var(--gray-600)',
                backgroundColor: 'var(--dark-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--text-xs, 11px)',
                fontWeight: 'var(--font-semibold, 600)',
                color: 'var(--gray-400)',
                transition: 'all 0.15s ease',
              }}
              title={`${remainingCount} more discrepanc${remainingCount === 1 ? 'y' : 'ies'}`}
            >
              +{remainingCount}
            </div>
          )}
        </div>
      ) : null}
      {discrepancies.length > 0 && (
        <DiscrepancyHoverCard
          discrepancies={discrepancies}
          isVisible={showHover}
          onDiscrepancyClick={onDiscrepancyClick}
          anchorElement={containerRef.current}
        />
      )}
    </div>
  );
};

export default DiscrepancyCell;
